import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import useDelete from '../../../../hooks/useDeleteN';
import { useSave } from '../../../../hooks/useSaveN';
import { COD_BASIC } from './index';
import {
  useDelete1,
  useSave1,
  useSearch1,
  useSearchCode,
  useSearchGroupUp,
} from './COD_BASIC_QUERY';
import { COD_BASIC_Schema } from './COD_BASIC_Schema';

const SCREEN_MENU_ID = 'Code';

const COD_BASIC_DUPLICATE_KEY_DEFS = [
  { field: 'groupCd', name: '그룹코드' },
  { field: 'code', name: '코드' },
];

const PickFieldCodBasicDuplicate = (item, field) => {
  if (!item || field == null) return undefined;
  const pascal = field.charAt(0).toUpperCase() + field.slice(1);
  return item[field] ?? item[pascal];
};

const BuildCodBasicDuplicateKey = (item) =>
  JSON.stringify(
    COD_BASIC_DUPLICATE_KEY_DEFS.map((k) =>
      (PickFieldCodBasicDuplicate(item, k.field)?.toString().trim() ?? '')
    )
  );

/** 공통 useCheckDuplicate 미사용 — code에 `_` 포함 시에도 groupCd·code 필드값으로 메시지 표시 */
const CheckDuplicateLogic1 = (dispatch, rows, changedRows) => {
  const rowKeys = new Set(rows.map(BuildCodBasicDuplicateKey).filter(Boolean));
  const newRows = changedRows.filter((f) => f.newYn === 'Y');
  const changedKeys = newRows.map(BuildCodBasicDuplicateKey).filter(Boolean);
  const duplicates = changedKeys.filter((k) => rowKeys.has(k));

  if (duplicates.length === 0) return true;

  const firstKey = duplicates[0];
  const exampleRow = newRows.find((f) => BuildCodBasicDuplicateKey(f) === firstKey);
  const exampleDisplay = COD_BASIC_DUPLICATE_KEY_DEFS.map((k) => {
    const v =
      PickFieldCodBasicDuplicate(exampleRow, k.field)?.toString().trim() ?? '';
    return `${k.name}: '${v}'`;
  }).join(', ');

  dispatch(
    showSnackbar({
      message: `중복된 데이터가 있습니다: ${duplicates.length}건 (${exampleDisplay})`,
      type: 'warning',
    })
  );
  return false;
};

function COD_BASICF() {
  const dispatch = useDispatch();
  const { userInfo, lanInfo } = useSelector((state) => state.BaseInfo);

  const gridRef = useRef();
  const selectedRowNosRef = useRef(new Set());
  const lastCheckedIndexRef = useRef(null);

  const [codeList, setCodeList] = useState([]);
  const [groupCdList, setGroupCdList] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [changedRowData, setChangedRowData] = useState([]);
  const [deletedRowData, setDeletedRowData] = useState([]);

  useSearchCode({ onSuccess: setCodeList });
  useSearchGroupUp({ onSuccess: setGroupCdList });

  const { refetch: SearchLogic1 } = useSearch1({
    onSuccess: (rows) => {
      const list = rows || [];
      if (!list.length) {
        setRowData([]);
        return;
      }
      setRowData(list);
    },
  });

  const saveMutation = useSave1({ userInfo });
  const deleteMutation = useDelete1();

  const deleteHandler1 = useDelete({
    gridRef,
    deleteMutation,
    updateDeleted: setDeletedRowData,
    updateRowData: setRowData,
    deletedRowData,
    rowData,
    setChangedRowData,
  });

  const saveCode = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '코드정보',
    schema: { fields: COD_BASIC_Schema.fields },
    getChangedRows: () => changedRowData.filter((r) => !r.deleteYn),
    getDeletedRows: () => deletedRowData,
    requestDelete: deleteHandler1.DeleteLogic,
    requestSave: saveMutation.runAsync,
    onSuccessRefresh: async ({ saveDetailData, deleteDetailData }) => {
      await SearchLogic1();
      const saveFail = (saveDetailData ?? []).some((r) => r?.isSuccess === false);
      const delFail = (deleteDetailData ?? []).some((r) => r?.isSuccess === false);
      if (saveFail || delFail) return;
      setChangedRowData([]);
      setDeletedRowData([]);
      setSelectedRowData([]);
      gridRef?.current?.api?.deselectAll?.();
    },
  });

  const RefreshLogic1 = async () => {
    setChangedRowData([]);
    setDeletedRowData([]);
    setSelectedRowData([]);
    gridRef?.current?.api?.deselectAll?.();
    await SearchLogic1();
  };

  const SaveLogic1 = async () => {
    const changedRows = changedRowData.filter((r) => !r.deleteYn);
    if (changedRows.length > 0) {
      const deletedNoSet = new Set((deletedRowData ?? []).map((r) => r.no));
      const rowDataForDuplicate = rowData.filter((r) => !deletedNoSet.has(r.no));
      if (!CheckDuplicateLogic1(dispatch, rowDataForDuplicate, changedRows)) return;
    }
    await saveCode.run();
  };

  /** MEN_BASIC / GON_BASIC / COM_BASIC — useDelete.MarkedForDeletion */
  const DeleteClick1 = () => {
    const newRowExists = selectedRowData.some((row) => row.newYn === 'Y');
    if (newRowExists) {
      dispatch(
        showSnackbar({
          message: '데이터 입력중입니다. 저장 후 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }
    deleteHandler1.MarkedForDeletion(selectedRowData);
  };

  const handleRowSelection = (rowDataSel) => {
    setSelectedRowData([rowDataSel]);
  };

  const OnSelectionChanged = () => {
    const gridApi = gridRef?.current?.api;
    const selectedNodes = gridApi?.getSelectedNodes() ?? [];
    const selectedData = selectedNodes.map((n) => n.data).filter(Boolean);
    selectedRowNosRef.current = new Set(selectedData.map((r) => r.no));
    setSelectedRowData(selectedData);
  };

  return (
    <div className="wrap">
      <COD_BASIC
        gridRef={gridRef}
        lanList={lanInfo}
        codeList={codeList}
        groupCdList={groupCdList}
        rowData={rowData}
        setRowData={setRowData}
        selectedRowData={selectedRowData}
        setSelectedRowData={setSelectedRowData}
        changedRowData={changedRowData}
        setChangedRowData={setChangedRowData}
        selectedRowNosRef={selectedRowNosRef}
        lastCheckedIndexRef={lastCheckedIndexRef}
        handleRowSelection={handleRowSelection}
        RestoreRow={deleteHandler1.RestoreRow}
        DeleteNewRow={deleteHandler1.DeleteNewRow}
        SearchLogic1={SearchLogic1}
        RefreshLogic1={RefreshLogic1}
        SaveLogic1={SaveLogic1}
        DeleteClick1={DeleteClick1}
        OnSelectionChanged={OnSelectionChanged}
      />
    </div>
  );
}

export default COD_BASICF;
